<template>
  <div>
    <VTooltip :content="itemTitle">
      <template v-if="props.item.type === 'label'">
        <div
          class="font-medium leading-6 text-neutral-950 rounded flex items-center"
          :class="[{ 'justify-center': props.item.align === 'center' }, theme.container, theme.label]"
          role="heading"
          aria-level="2"
        >
          {{ props.item.label }}
        </div>
      </template>
      <template v-else>
        <component
          :is="tagName"
          class="flex items-center w-full whitespace-nowrap"
          :disabled="props.item.disabled"
          :class="[computedWrapperClass, theme.container, theme.button.base]"
          v-bind="computedAttributes"
          data-component-name="VSContextMenuItem"
          v-on="computedListeners"
        >
          <slot />
          <VIcon v-if="props.item.icon" :size="theme.icon" :name="props.item.icon" :class="{ 'opacity-50' : isCheckbox && isDisabled }" />
          <div class="overflow-ellipsis overflow-hidden" :class="{ 'opacity-50' : isCheckbox && isDisabled }">
            {{ props.item.label }}
          </div>
          <input
            v-if="isCheckbox"
            :checked="getItemValue(props.item as V.ContextMenu.Checkbox)"
            type="checkbox"
            :disabled="isDisabled"
            class="h-4 w-4 rounded border-neutral-300 focus:ring-blue-500 text-primary-500 disabled:text-neutral-200 ml-auto pointer-events-none"
            :tabindex="-1"
            @click="onCheckboxClick"
          >
          <VIcon v-if="props.item.type === 'menu'" :size="theme.icon" class="ml-auto" name="Solid/chevron-right-v2" />
        </component>
      </template>
    </VTooltip>
  </div>
</template>
 
<script setup generic="T" lang="ts">
import { computed } from 'vue';
import type { V } from '@component-utils/types';
import VIcon from '@component-library/labels/VIcon.vue';
import { getItemValue } from '@component-utils/utils';
import { useTheme } from '@component-library/themes/themes';
import type { ThemeProp } from '@component-library/themes/types';
import VTooltip from '../VTooltip.vue';

defineOptions({
  name: 'VSContextMenuItem'
})

const props = defineProps<{
  item: V.ContextMenu.Button<T> | V.ContextMenu.Checkbox | V.ContextMenu.Menu<T> | V.ContextMenu.Label
  context?: T
  theme?: ThemeProp<'VSContextMenuItem'>
}>()

const theme = useTheme(props, 'VSContextMenuItem')

const emit = defineEmits<{
  close: []
}>()

const tagName = computed(() => {
  switch (props.item.type) {
    case 'button': return 'href' in props.item ? 'a' : 'button'
    case 'checkbox': return 'label'
    default: return 'button'
  }
})

const isCheckbox = computed(() => props.item.type === 'checkbox')

const isDisabled = computed(() => 'disabled' in props.item && props.item.disabled)

const computedAttributes = computed(() => {
  switch (props.item.type) {
    case 'button': {
      return {
        type: 'button',
        href: 'href' in props.item ? props.item.href : undefined
      }
    }
    case 'checkbox': {
      return {
        tabindex: props.item.disabled ? undefined : 0
      }
    }
    default: {
      return {}
    }
  }
})

const itemTitle = computed(() => ('title' in props.item && props.item.title || undefined) ?? props.item.label)

const onCheckboxClick = () => {
  if (isDisabled.value) return

  const item = props.item as V.ContextMenu.Checkbox

  const value = getItemValue(item)
  if (typeof item.value === 'boolean') {
    item.value = !value
  } else {
    item.value.set(!value)
  }
}

const itemColor = computed(() => 'color' in props.item ? props.item.color ?? 'default' : 'default')

const computedListeners = computed(() => {
  const item = props.item

  if (item.type === 'button' && !('href' in item)) {
    return {
      click () {
        item.action(props.context)

        emit('close')
      }
    }
  } else if (item.type === 'checkbox') {
    return {
      keydown (event: KeyboardEvent) {
        if (event.code !== 'Space' && event.code !== 'Enter') return

        onCheckboxClick()
      }
    }
  } else {
    return {}
  }
})

const computedWrapperClass = computed (() => {
  return {
    'cursor-not-allowed': isDisabled.value, 
    [theme.value.button.colors[itemColor.value].disabled]: isDisabled.value,
    [theme.value.button.colors[itemColor.value].enabled]: !isDisabled.value,
    'cursor-pointer': isCheckbox.value && !isDisabled.value,
    [theme.value.menu]: props.item.type === 'menu'
  }
})
</script>
